import React from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { useMapStore } from '../stores/useMapStore';
import { useProceduralStore } from '../stores/useProceduralStore';

interface GenerateTerrainButtonProps {
  onGenerate: () => void;
  isGenerating?: boolean;
}

export const GenerateTerrainButton: React.FC<GenerateTerrainButtonProps> = React.memo(GenerateTerrainButtonRaw);

function GenerateTerrainButtonRaw({
  onGenerate,
  isGenerating = false,
}: GenerateTerrainButtonProps) {
  const isBboxValid = useMapStore((s) => s.isBboxValid());
  const bboxArea = useMapStore((s) => s.getBboxAreaKm2());
  const generationProgress = useProceduralStore((s) => s.generationProgress);

  if (!isBboxValid) return null;

  return (
    <div className="absolute bottom-6 left-1/2 z-20 -translate-x-1/2">
      <button
        onClick={onGenerate}
        disabled={isGenerating}
        className={[
          'relative flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold shadow-xl backdrop-blur-sm border transition-all min-h-touch overflow-hidden',
          isGenerating
            ? 'bg-slate-700/90 text-slate-400 border-slate-600/50 cursor-wait'
            : 'bg-sky-600/90 text-white border-sky-500/50 hover:bg-sky-500/90 active:scale-95',
        ].join(' ')}
      >
        {isGenerating ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Analyzing…
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4" />
            Analyze This Site
          </>
        )}
        {bboxArea !== null && !isGenerating && (
          <span className="ml-1 text-xs font-normal text-sky-200">
            ({bboxArea < 1 ? `${(bboxArea * 100).toFixed(1)} ha` : `${bboxArea.toFixed(2)} km²`})
          </span>
        )}
        {isGenerating && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-900/50">
            <div
              className="h-full bg-sky-300 transition-all duration-300"
              style={{ width: `${(generationProgress || 0) * 100}%` }}
            />
          </div>
        )}
      </button>
    </div>
  );
};
